import { imageUrl } from '../../common/constants/ProductOfferingConstants.js';
import { dsClass } from '../../common/constants/dsClasses.js';
import { formatProductOfferingPrice } from '../../common/utils/priceUtilsCoCo.js';
import {
  getLocalizedString,
  getProductOfferingPriceDataWithFallback,
} from '../../common/utils/productOfferingsUtils.js';
import { ProductOfferingPrice } from '../../generated/api/productOfferingPrice.js';
import type { BundleAddonGroup } from '../../generated/api/bundleAddonGroup.js';
import type { CartBundleAddonGroup } from '../../generated/api/cartBundleAddonGroup.js';

import './BundleAddonGroupRadioCardCoCo.scss';

// TODO: Replace with actual device image URL from product offering data once available from API/CMS
const DEVICE_IMAGE_URL = imageUrl;

interface RadioCardItemProps {
  id: string;
  radioGroupName: string;
  value: string;
  checked: boolean;
  isSelected: boolean;
  imageAlt: string;
  label: string;
  price: string;
  onChange: () => void;
}

const RadioCardItem = ({
  id,
  radioGroupName,
  value,
  checked,
  isSelected,
  imageAlt,
  label,
  price,
  onChange,
}: RadioCardItemProps) => (
  <div className={`of-equipment-radio-card__item ${isSelected ? 'of-equipment-radio-card__item--selected' : ''}`}>
    <div className="ds-radio">
      <input type="radio" id={id} name={radioGroupName} value={value} checked={checked} onChange={onChange} />
      <label className="ds-radio__label" htmlFor={id}>
        <span className="ds-radio__icon" />
        <div className="of-equipment-radio-card__content">
          <img className="of-equipment-radio-card__image" src={DEVICE_IMAGE_URL} alt={imageAlt} />
          <span className="of-equipment-radio-card__name">{label}</span>
          <span className="of-equipment-radio-card__price">{price}</span>
        </div>
      </label>
    </div>
  </div>
);

export interface BundleAddonGroupRadioCardCoCoProps {
  group: BundleAddonGroup | CartBundleAddonGroup;
  selectedOfferingIds: string[];
  onSelectionChange: (groupId: string, selectedIds: string[]) => void;
  errorMessage?: string;
}

export const BundleAddonGroupRadioCardCoCo = ({
  group,
  selectedOfferingIds,
  onSelectionChange,
  errorMessage,
}: BundleAddonGroupRadioCardCoCoProps) => {
  const offerings = group.productOffering ?? [];
  const groupName = getLocalizedString(group.name);
  const radioGroupName = `equipment-group-${group.id}`;

  if (offerings.length === 0) {
    return null;
  }

  return (
    <div className="of-equipment-radio-card">
      <div className="of-equipment-radio-card__subheading">{groupName}</div>
      <fieldset className="of-equipment-radio-card__fieldset" aria-label={groupName}>
        {offerings.map(offering => {
          const isSelected = selectedOfferingIds[0] === offering.id;
          return (
            <RadioCardItem
              key={offering.id}
              id={`${radioGroupName}-${offering.id}`}
              radioGroupName={radioGroupName}
              value={offering.id}
              checked={isSelected}
              isSelected={isSelected}
              imageAlt={getLocalizedString(offering.name)}
              label={getLocalizedString(offering.name)}
              price={formatProductOfferingPrice(
                getProductOfferingPriceDataWithFallback(
                  offering.productOfferingPrice,
                  ProductOfferingPrice.PriceTypeEnum.OC,
                  ProductOfferingPrice.PriceTypeEnum.RC
                )
              )}
              onChange={() => onSelectionChange(group.id, [offering.id])}
            />
          );
        })}
      </fieldset>
      {errorMessage && (
        <div className={`${dsClass.TEXT_XS} ${dsClass.MARGIN_TOP_1} ${dsClass.TEXT_COLOR_ERROR}`}>{errorMessage}</div>
      )}
    </div>
  );
};
